import React, { useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "../../hooks/use-toast";
import { copyText } from "../../lib/copy";
import { Copy, ExternalLink, Gift } from "lucide-react";

const card = "bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]";

export default function WidgetTab() {
  const { user } = useAuth();
  const biz = user?.business || {};
  const [label, setLabel] = useState(biz.widget_label || "Buy a gift voucher");
  const [mode, setMode] = useState(biz.widget_mode || "floating");
  const origin = window.location.origin;
  const snippet = mode === "inline"
    ? `<div id="giftvouchers-button"></div>\n<script src="${origin}/widget.js" data-store="${biz.slug}" async></script>`
    : `<script src="${origin}/widget.js" data-store="${biz.slug}" async></script>`;

  const save = async () => {
    try { await api.put("/business/me", { widget_label: label, widget_mode: mode }); toast({ title: "Widget saved" }); }
    catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  return (
    <div className="space-y-6 max-w-[760px]" data-testid="widget-tab">
      <div className={card}>
        <h3 className="font-semibold text-[#16243b]">Your button</h3>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Button text</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} data-testid="widget-label-input"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Where it shows</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="widget-mode-select"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
              <option value="floating">Floating, bottom-right of every page</option>
              <option value="inline">Inside my page, where I paste it</option>
            </select>
          </div>
        </div>
        <div className="mt-5 rounded-xl bg-[#f6f8fb] p-6 flex items-center justify-center" data-testid="widget-preview">
          <span className="rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 shadow-lg"
            style={{ background: biz.button_color || "#2ec9c9", color: biz.button_text_color || "#fff" }}><Gift size={16} /> {label}</span>
        </div>
        <button onClick={save} data-testid="widget-save-btn" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium mt-4">Save</button>
      </div>
      <div className={card}>
        <h3 className="font-semibold text-[#16243b]">Paste this into your website</h3>
        <p className="text-sm text-[#5a6b80] mt-1">Add it once, just before <code>&lt;/body&gt;</code> (or in a custom HTML block on Wix, Squarespace or WordPress). Visitors buy without leaving your site.</p>
        <pre className="mt-4 bg-[#16243b] text-[#c8f3f3] text-xs rounded-xl p-4 overflow-x-auto whitespace-pre-wrap" data-testid="widget-snippet">{snippet}</pre>
        <div className="flex gap-3 mt-3">
          <button onClick={() => copyText(snippet, "Code copied")} data-testid="widget-copy-btn"
            className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Copy size={15} /> Copy code</button>
          <a href={`/widget-demo.html?store=${biz.slug}`} target="_blank" rel="noreferrer" data-testid="widget-demo-link"
            className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><ExternalLink size={15} /> Try it on a test page</a>
        </div>
        <p className="text-xs text-[#8a97a8] mt-3">Save your changes first — the button reads your latest text and colours.</p>
      </div>
    </div>
  );
}
